import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * Opens a persistent WebSocket to /api/ws/chat.
 * Reconnects on close with exponential backoff.
 * onMessage: callback(payload, sendFn) when server sends JSON frames.
 * The sendFn allows the consumer to send JSON frames back to the server
 * (e.g. typing events).
 */
export function useChatSocket(onMessage) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  const sendFn = useCallback((obj) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    try { ws.send(JSON.stringify(obj)); return true; } catch (_e) { return false; }
  }, []);

  const connect = useCallback(() => {
    if (!user) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = base.replace(/^http/, "ws") + "/api/ws/chat";
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { retryRef.current = 0; handlerRef.current?.({ type: "open" }, sendFn); };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          handlerRef.current?.(data, sendFn);
        } catch (_e) {}
      };
      ws.onclose = () => {
        if (closedRef.current) return;
        const delay = Math.min(10000, 500 * Math.pow(2, retryRef.current++));
        setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch (_e) {} };
    } catch (_e) {
      const delay = Math.min(10000, 500 * Math.pow(2, retryRef.current++));
      setTimeout(connect, delay);
    }
  }, [user, sendFn]);

  useEffect(() => {
    closedRef.current = false;
    connect();
    return () => {
      closedRef.current = true;
      try { wsRef.current?.close(); } catch (_e) {}
    };
  }, [connect]);
}
